
























































import { computed, defineComponent, ref, PropType } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import { printPaymentPackage } from "@/lib/moneyHelpers";
import config from "@/config";
import { FormFields, FormValues, ListFields } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";

export default defineComponent({
  name: "PriceSettingsView",
  components: { BasePage, PageTitle, BaseForm, BaseButton },
  props: {
    loading: { type: Boolean, required: true },
    callPaymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    messagePaymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    privateLivePaymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    callValues: { type: Object as PropType<FormValues>, required: true },
    messageValues: { type: Object as PropType<FormValues>, required: true },
    privateLiveValues: { type: Object as PropType<FormValues>, required: true },
  },
  setup(props, { emit }) {
    const { authorities } = useAuthUser();
    const saving = ref(false);

    const canUpdateCallOptions = computed(
      () => checkAuthorities(authorities.value, ["updateMyCallOptions"]) && config.featureCalling
    );
    const canUpdateCallPrice = computed(() => checkAuthorities(authorities.value, ["updateMyCallPrice"]));
    const canUpdateMessagePrice = computed(
      () => checkAuthorities(authorities.value, ["updateMyMessagePrice"]) && config.featurePaidSentMessage
    );
    const isAudioMessageEnabled = computed(() => config.featureAudioMessageAttachments);

    const textMessageValues = computed<FormValues>(() => ({
      perSentPriceId: props.messageValues.perSentPriceId,
      subscriberPerSentPriceId: props.messageValues.subscriberPerSentPriceId,
    }));

    const mediaMessageValues = computed<FormValues>(() => ({
      perSentWithMediaPriceId: props.messageValues.perSentWithMediaPriceId,
      subscriberPerSentWithMediaPriceId: props.messageValues.subscriberPerSentWithMediaPriceId,
    }));

    const audioMessageValues = computed<FormValues>(() => ({
      perSentWithAudioPriceId: props.messageValues.perSentWithAudioPriceId,
      subscriberPerSentWithAudioPriceId: props.messageValues.subscriberPerSentWithAudioPriceId,
    }));

    const privateShowValues = computed<FormValues>(() => ({
      privateLivePricePackageId: props.privateLiveValues.privateLiveSelectedPackageId,
    }));

    const perMinuteCallPrices = computed<ListFields>(() => [
      { value: "0", text: $t("Free") },
      ...props.callPaymentPackages.map(item => ({ value: item.id, text: printPaymentPackage(item) })),
    ]);

    const perSentMessagePrices = computed<ListFields>(() => [
      { value: "0", text: $t("Free") },
      ...props.messagePaymentPackages.map(item => ({ value: item.id, text: printPaymentPackage(item) })),
    ]);

    const privateShowPrices = computed<ListFields>(() => {
      return [
        { value: "0", text: $t("Free") },
        ...props.privateLivePaymentPackages.map(item => ({ value: item.id, text: printPaymentPackage(item) })),
      ];
    });

    const callsFields = computed<FormFields>(() => [
      {
        class: "__call-enabling fw-600",
        name: "incomingCallsEnabled",
        label: $t("Calls"),
        type: "switch",
      },
      ...(canUpdateCallPrice.value
        ? [
            {
              class: "__call-price",
              name: "callPricePackageId",
              disabled: !props.callValues.incomingCallsEnabled,
              label: $t("Minute of call"),
              type: "select",
              options: perMinuteCallPrices.value,
            },
          ]
        : []),
    ]);

    const textMessagesFields = computed<FormFields>(() => [
      {
        class: "__per-message",
        name: "perSentPriceId",
        label: $t("For non-subscribers"),
        type: "select",
        options: perSentMessagePrices.value,
      },
      {
        class: "__per-message-subscriber",
        name: "subscriberPerSentPriceId",
        label: $t("For subscribers"),
        type: "select",
        options: perSentMessagePrices.value,
      },
    ]);

    const mediaMessagesFields = computed<FormFields>(() => [
      {
        class: "__per-media-message",
        name: "perSentWithMediaPriceId",
        label: $t("For non-subscribers"),
        type: "select",
        options: perSentMessagePrices.value,
      },
      {
        class: "__per-media-message-subscriber",
        name: "subscriberPerSentWithMediaPriceId",
        label: $t("For subscribers"),
        type: "select",
        options: perSentMessagePrices.value,
      },
    ]);

    const audioMessagesFields = computed<FormFields>(() => [
      {
        class: "__per-audio-message",
        name: "perSentWithAudioPriceId",
        label: $t("For non-subscribers"),
        type: "select",
        options: perSentMessagePrices.value,
      },
      {
        class: "__per-audio-message-subscriber",
        name: "subscriberPerSentWithAudioPriceId",
        label: $t("For subscribers"),
        type: "select",
        options: perSentMessagePrices.value,
      },
    ]);

    const privateShowFields = computed<FormFields>(() => [
      {
        class: "__private-live-price",
        name: "privateLivePricePackageId",
        label: $t("Price per minute of show"),
        type: "select",
        options: privateShowPrices.value,
      },
    ]);

    const priceById = (id: string | number | null | undefined) => {
      if (!id) return 0;
      return props.messagePaymentPackages.find(p => p.id === id)?.amount ?? 0;
    };

    const normalizeMessageValues = (previous: FormValues, partial: FormValues) => {
      const nextValues = { ...previous, ...partial };

      const isPerSentValueChanged = nextValues.perSentPriceId !== previous.perSentPriceId;
      const isPerSentWithMediaValueChanged = nextValues.perSentWithMediaPriceId !== previous.perSentWithMediaPriceId;
      const isPerSentWithAudioValueChanged = nextValues.perSentWithAudioPriceId !== previous.perSentWithAudioPriceId;
      const isSubscriberPerSentValueChanged = nextValues.subscriberPerSentPriceId !== previous.subscriberPerSentPriceId;
      const isSubscriberPerSentWithMediaValueChanged =
        nextValues.subscriberPerSentWithMediaPriceId !== previous.subscriberPerSentWithMediaPriceId;
      const isSubscriberPerSentWithAudioValueChanged =
        nextValues.subscriberPerSentWithAudioPriceId !== previous.subscriberPerSentWithAudioPriceId;

      const textMessagePrice = priceById(nextValues.perSentPriceId);
      const mediaMessagePrice = priceById(nextValues.perSentWithMediaPriceId);
      const audioMessagePrice = priceById(nextValues.perSentWithAudioPriceId);
      const subscriberTextMessagePrice = priceById(nextValues.subscriberPerSentPriceId);
      const subscriberMediaMessagePrice = priceById(nextValues.subscriberPerSentWithMediaPriceId);
      const subscriberAudioMessagePrice = priceById(nextValues.subscriberPerSentWithAudioPriceId);

      if (isPerSentValueChanged) {
        if (textMessagePrice > mediaMessagePrice) {
          nextValues.perSentWithMediaPriceId = nextValues.perSentPriceId;
        }
        if (textMessagePrice > audioMessagePrice) {
          nextValues.perSentWithAudioPriceId = nextValues.perSentPriceId;
        }
      } else if (isPerSentWithMediaValueChanged && mediaMessagePrice < textMessagePrice) {
        nextValues.perSentPriceId = nextValues.perSentWithMediaPriceId;
      } else if (isPerSentWithAudioValueChanged && audioMessagePrice < textMessagePrice) {
        nextValues.perSentPriceId = nextValues.perSentWithAudioPriceId;
      } else if (isSubscriberPerSentValueChanged) {
        if (subscriberTextMessagePrice > subscriberMediaMessagePrice) {
          nextValues.subscriberPerSentWithMediaPriceId = nextValues.subscriberPerSentPriceId;
        }
        if (subscriberTextMessagePrice > subscriberAudioMessagePrice) {
          nextValues.subscriberPerSentWithAudioPriceId = nextValues.subscriberPerSentPriceId;
        }
      } else if (isSubscriberPerSentWithMediaValueChanged && subscriberMediaMessagePrice < subscriberTextMessagePrice) {
        nextValues.subscriberPerSentPriceId = nextValues.subscriberPerSentWithMediaPriceId;
      } else if (isSubscriberPerSentWithAudioValueChanged && subscriberAudioMessagePrice < subscriberTextMessagePrice) {
        nextValues.subscriberPerSentPriceId = nextValues.subscriberPerSentWithAudioPriceId;
      }

      return nextValues;
    };

    const onInputMessages = (partial: FormValues) => {
      const normalizedValues = normalizeMessageValues(props.messageValues, partial);
      emit("inputMessages", normalizedValues);
    };

    const onInputCalls = (values: FormValues) => {
      emit("inputCalls", values);
    };

    const onInputPrivateLive = (values: FormValues) => {
      emit("inputPrivateLive", values);
    };

    const onSave = () => {
      saving.value = true;
      emit("submit", () => {
        saving.value = false;
      });
    };

    return {
      canUpdateCallOptions,
      canUpdateCallPrice,
      canUpdateMessagePrice,
      isAudioMessageEnabled,
      saving,
      callsFields,
      textMessagesFields,
      textMessageValues,
      mediaMessagesFields,
      mediaMessageValues,
      audioMessagesFields,
      audioMessageValues,
      privateShowValues,
      privateShowFields,
      onInputPrivateLive,
      onSave,
      onInputCalls,
      onInputMessages,
    };
  },
});
